import React, { useCallback } from 'react';
import { Button, Box, spacing } from '@landr/maestro';
import styled from 'styled-components';
import { analytics } from 'utils/analytics';
import {
    CtaGoal,
    CtaType,
    InteractionSource,
} from 'utils/analytics/AnalyticsV2Service.generated';
import { useAuthentication } from 'components/AuthenticationProvider/AuthenticationProvider';
import { useExecutionEnvironmentContext } from 'contexts/ExecutionEnvironmentContext/ExecutionEnvironmentContext';
import { getReturnUrl } from 'helpers';
import { TwoColumns } from 'components/TwoColumns/TwoColumns';
import BackgroundImageFromAsset from 'components/BackgroundImageFromAsset/BackgroundImageFromAsset';
import { PricingCardContent, Included } from '../types';
import IncludedGroup from './IncludedGroup';
import Title from './Title';
import Description from './Description';

export const usePricingCard = ({
    content,
    isAuthenticated,
    isDAWPlugin,
}: {
    content: PricingCardContent;
    isAuthenticated: boolean;
    isDAWPlugin: boolean;
}) => {
    const {
        byline,
        ctaCopy,
        ctaLinkAuthenticated,
        ctaLinkGuest,
        filetype,
        heading,
        image,
        included,
    } = content;

    const ctaLinkWithoutReturnUrl = isAuthenticated
        ? ctaLinkAuthenticated
        : ctaLinkGuest;

    const ctaLink = `${ctaLinkWithoutReturnUrl}?returnUrl=${getReturnUrl()}`;

    const handleOnClick = useCallback(() => {
        analytics.trackCTAClicked({
            ctaGoal: CtaGoal.ViewPricing,
            ctaType: CtaType.Card,
            interactionSource: InteractionSource.CardAction,
            ctaDestination: ctaLink,
            ctaText: ctaCopy,
        });

        isDAWPlugin
            ? window.open(ctaLink, '_blank')
            : window.location.assign(ctaLink);
    }, [isDAWPlugin, ctaLink, ctaCopy]);

    return {
        byline,
        ctaCopy,
        filetype,
        heading,
        image,
        included,
        handleOnClick,
    };
};

const Wrapper = styled(Box)`
    padding: ${spacing('mg')};

    ${({ theme }): string => theme.breakpoint('lg')`       
        padding: ${spacing('jb')} ${spacing('lg')};
    `}
`;

export const _PricingCard = ({
    byline,
    ctaCopy,
    direction,
    filetype,
    heading,
    image,
    included,
    isLoading,
    isMobile,
    handleOnClick,
}: {
    byline: string;
    ctaCopy: string;
    direction: 'row' | 'row-reverse';
    filetype: string;
    heading: string;
    included: Included[];
    isLoading: boolean;
    isMobile: boolean;
    image: string;
    handleOnClick: () => void;
}) => (
    <TwoColumns
        direction={isMobile ? 'column' : direction}
        columnRatio={isMobile ? 'full' : 'half'}
        coloredColumn="info"
    >
        <TwoColumns.Info>
            <Wrapper>
                <Title
                    isLoading={isLoading}
                    content={heading}
                    isMobile={isMobile}
                />
                <Description
                    isLoading={isLoading}
                    content={byline}
                    isMobile={isMobile}
                />
                <IncludedGroup
                    isLoading={isLoading}
                    items={included}
                    isMobile={isMobile}
                />
                <Button
                    isRounded
                    isLoading={isLoading}
                    onClick={handleOnClick}
                    size={isMobile ? 'md' : 'lg'}
                >
                    {ctaCopy}
                </Button>
            </Wrapper>
        </TwoColumns.Info>
        {!isMobile && (
            <TwoColumns.Media>
                <BackgroundImageFromAsset
                    isLoading={isLoading}
                    image={image}
                    filetype={filetype}
                    lazyLoading={true}
                />
            </TwoColumns.Media>
        )}
    </TwoColumns>
);

const PricingCard = ({
    direction,
    content,
    isMobile,
    isLoading,
}: {
    direction: 'row' | 'row-reverse';
    content: PricingCardContent;
    isMobile: boolean;
    isLoading: boolean;
}) => {
    const { isAuthenticated } = useAuthentication();
    const { isDAWPlugin } = useExecutionEnvironmentContext();
    const {
        heading,
        byline,
        included,
        ctaCopy,
        image,
        filetype,
        handleOnClick,
    } = usePricingCard({ content, isAuthenticated, isDAWPlugin });

    return (
        <_PricingCard
            isLoading={isLoading}
            direction={direction}
            isMobile={isMobile}
            heading={heading}
            byline={byline}
            included={included}
            ctaCopy={ctaCopy}
            image={image}
            filetype={filetype}
            handleOnClick={handleOnClick}
        />
    );
};

export default PricingCard;
